import { csvDownloadFileName } from "@components/report/widget/widget-statistics/helper";
import { TypeTimeInterval } from "@custom-types/Types";
import { useChartFilterInterval } from "@hooks/useChartFilterInterval";
import useOrganization from "@hooks/useOrganization";
import { AnalyticsFilterRules } from "@queries/Analytics";
import { useGetAnalyticsChartQuery } from "@queries/tanstack/analytics/AnalyticsQuery";
import {
  ANALYTICS_CHART_TYPE,
  VIEW_TYPE_MAPPING,
  WIDGET_TYPE
} from "@resources/Constants";
import moment from "moment";
import { useEffect, useState } from "react";
import {
  BaseTimeProps,
  ChartRules
} from "src/pages/analytics/chart/ChartCard/Interfaces";

const useAnalyticsData = (
  type: string,
  baseTime: BaseTimeProps,
  viewType: string,
  rules: ChartRules,
  isTypeInterval?: boolean,
  title?: string,
  selected?: string[]
) => {
  const { orgId } = useOrganization();
  const [dataSeries, setDataSeries] = useState<string[]>([]);
  const {
    getTypeTime: getTypeInterval,
    mapTypeToTimeInterval,
    time: timeInterval,
    type: typeInterval,
    setType
  } = useChartFilterInterval({
    baseTime,
    ...(isTypeInterval
      ? { isForPeopleAnalytics: true }
      : { isForAnalytics: true })
  });
  const {
    data: analyticsDatas,
    isLoading,
    getChartData,
    getCSVDownloadAnalytics
  } = useGetAnalyticsChartQuery(
    type,
    viewType,
    selected,
    csvDownloadFileName(baseTime, title ?? "")
  );

  const { getCategoryData, categoryData, isCategoryDataLoading } =
    useGetAnalyticsChartQuery(type);

  useEffect(() => {
    if (analyticsDatas && analyticsDatas.length > 0) {
      const series = analyticsDatas
        ? analyticsDatas
            .map((data) => data.locationName)
            .filter((name): name is string => name !== undefined)
        : [""];
      setDataSeries(series);
    }
  }, [analyticsDatas]);

  const convertRule = (rules: ChartRules, isSingleLocation?: boolean) => {
    let formattedRules: AnalyticsFilterRules = {};
    Object.keys(rules).forEach((key) => {
      formattedRules = {
        ...formattedRules,
        [key]: rules[key].map((rule) => ({
          deviceId: rule.deviceId,
          ruleName: rule.ruleName,
          channel: rule.channel,
          ...(isSingleLocation ? { cameraName: rule.cameraName } : {})
        }))
      };
    });
    return formattedRules;
  };

  const updatedAnalytics = (
    types: string[] = [],
    selectedInterval: TypeTimeInterval | null = null,
    changedViewType: string | null = null,
    isSingleLocation = false
  ) => {
    const timeInterval = selectedInterval
      ? mapTypeToTimeInterval(selectedInterval)
      : (getTypeInterval({ baseTime }) as string);
    const viewTypeConverted = VIEW_TYPE_MAPPING[changedViewType || viewType];
    timeInterval &&
      getChartData({
        endDateTime: moment(baseTime.end).endOf("d").toISOString(true),
        startDateTime: moment(baseTime.start).startOf("d").toISOString(true),
        orgId,
        timeInterval,
        vehicleType: types,
        rules: convertRule(rules, isSingleLocation),
        cumulativeType: viewTypeConverted ?? "",
        hasSingleLocation: isSingleLocation
      });
  };

  const getWidgetType = (type: string) => {
    switch (type) {
      case ANALYTICS_CHART_TYPE.CUMULATIVE_PEOPLE_COUNT:
        return WIDGET_TYPE.CUMULATE_PEOPLECOUNTS;
      case ANALYTICS_CHART_TYPE.PEOPLE_OCCUPANCY:
        return WIDGET_TYPE.STAYING_PEOPLECOUNTS;
      case ANALYTICS_CHART_TYPE.PEOPLE_QUEUES:
        return WIDGET_TYPE.CHECKOUT_QUEUE;
      case ANALYTICS_CHART_TYPE.SLIP_AND_FALL:
        return WIDGET_TYPE.SLIP_AND_FALL_STATISTICS;
      case ANALYTICS_CHART_TYPE.CROWD_COUNTING:
        return WIDGET_TYPE.CROWD_SAFETY_STATISTICS;
      case ANALYTICS_CHART_TYPE.VEHICLE_QUEUES:
        return WIDGET_TYPE.VEHICLE_QUEUE;
      case ANALYTICS_CHART_TYPE.CUMULATIVE_SHOPPING_CART_COUNT:
        return WIDGET_TYPE.CUMULATE_SHOPPING_CART_COUNTS;
      case ANALYTICS_CHART_TYPE.SHOPPING_CARTS_IN_USE:
        return WIDGET_TYPE.SHOPPING_CART_COUNT;
      case ANALYTICS_CHART_TYPE.SHOPPING_CART_QUEUES:
        return WIDGET_TYPE.SHOPPING_CART_CHECKOUT_QUEUE;
      case ANALYTICS_CHART_TYPE.CUMULATIVE_VEHICLE_COUNT:
        return WIDGET_TYPE.CUMULATE_VEHICLECOUNTS;
      case ANALYTICS_CHART_TYPE.VEHICLE_OCCUPANCY:
        return WIDGET_TYPE.STAYING_VEHICLECOUNTS;
      case ANALYTICS_CHART_TYPE.STOPPED_VEHICLE_ANALYTICS:
        return WIDGET_TYPE.STOPPED_VEHICLE_STATISTICS;
      case ANALYTICS_CHART_TYPE.SLOW_TRAFFIC:
        return WIDGET_TYPE.SLOW_TRAFFIC_STATISTICS;
      default:
        return "";
    }
  };
  const analyticsCSVDownload = (isSingleLocation: boolean) => {
    const widgetType = getWidgetType(type);
    widgetType &&
      getCSVDownloadAnalytics({
        endDateTime: moment(baseTime.end).endOf("d").toISOString(true),
        startDateTime: moment(baseTime.start).startOf("d").toISOString(true),
        orgId,
        widgetType,
        rules: convertRule(rules, isSingleLocation),
        hasSingleLocation: isSingleLocation
      });
  };

  const updatedCategoryAnalytics = (activeTab: string) => {
    getCategoryData({
      endDateTime: moment(baseTime.end).endOf("d").toISOString(true),
      startDateTime: moment(baseTime.start).startOf("d").toISOString(true),
      orgId,
      timeInterval: baseTime.mode, // Added timeInterval
      rules: convertRule(rules),
      category: activeTab
    });
  };

  return {
    analyticsDatas,
    dataSeries,
    isLoading,
    updatedAnalytics,
    categoryData,
    isCategoryDataLoading,
    updatedCategoryAnalytics,
    analyticsCSVDownload,
    timeInterval,
    typeInterval,
    setType
  };
};

export default useAnalyticsData;
